// @flow strict-local

import * as React from 'react';

import eachWeekOfInterval from 'date-fns/eachWeekOfInterval';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import add from 'date-fns/add';
import range from 'lodash/range';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';

import classify from 'src/utils/classify';
import useI18n from 'src/hooks/useI18n';

import css from './calendar.css';


export const Grid = ({
  monthIndex,
  year,
  starts = 0,
  onDateClick,
  eventDates = [],
  selectedDate,
  setSelectedDate,
  color,
}: {
  monthIndex: number,
  year: number,
  onDateClick?: (date: Date) => mixed,
  eventDates?: Date[], // these are numerical dates, 1-indexed
  starts?: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  selectedDate?: Date,
  setSelectedDate?: (d: Date) => mixed,
  color?: ?string,
}): React.Element<'div'> => {
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = lastDayOfMonth(firstDay);
  const weeks = eachWeekOfInterval(
    {start: firstDay, end: lastDay},
    {weekStartsOn: starts},
  );

  return (
    <div className={css.calendarGrid}>
      {weeks.map((firstDay, _index) => (
        <WeekGrid
          key={firstDay.toISOString()}
          firstDay={firstDay}
          monthIndex={monthIndex}
          eventDates={eventDates}
          selectedDate={selectedDate}
          onDateClick={onDateClick}
          setSelectedDate={setSelectedDate}
          color={color}
        />
      ))}
    </div>
  );
};

export const WeekGrid = ({
  className,
  firstDay,
  eventDates,
  selectedDate,
  onDateClick,
  setSelectedDate,
  monthIndex,
  color,
}: {
  className?: string,
  firstDay: Date,
  eventDates: Date[],
  selectedDate?: Date,
  monthIndex?: number,
  onDateClick?: (Date) => mixed,
  setSelectedDate?: (Date) => mixed,
  color?: ?string,
}): React.Element<'div'> => {
  const days = range(7).map((days) => add(firstDay, {days}));
  return (
    <div className={classify(css.weekRow, className)}>
      {days.map((day) => {
        const dateString = format(day, 'd');
        const hasEvent = eventDates.some((eventDate) =>
          isSameDay(day, eventDate),
        );

        const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;

        const className = classify(css.calendarDate, {
          [css.currentMonthDate]:
            monthIndex != null ? day.getMonth() === monthIndex : true,
          [css.hasEvent]: hasEvent,
          [css.selectedDate]: isSelected,
        });

        const style = {};
        if (color) {
          if (isSelected) {
            style.backgroundColor = color;
          } else if (hasEvent) {
            style.color = color;
          }
        }

        return (
          <div key={day.toISOString()} className={className} title={day}>
            {hasEvent ? (
              <button
                onClick={() => {
                  onDateClick?.(day);
                  setSelectedDate?.(day);
                }}
                style={style}
              >
                {dateString}
              </button>
            ) : (
              <span style={style}>{dateString}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const CalendarHeader = ({
  className = 'medium',
  date = new Date(),
  starts = 0,
}: {
  className?: string,
  date: Date,
  starts?: 0 | 1 | 2 | 3 | 4 | 5 | 6,
}): React.Element<'div'> => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const week = eachWeekOfInterval(
    {start: date, end: date},
    {weekStartsOn: starts},
  );
  const days = range(7).map((days) => add(week[0], {days}));
  return (
    <div className={className}>
      {days.map((day) => {
        const formattedDay = format(day, 'EEE');
        return (
          <div className={css.dayHeader} key={formattedDay}>
            {t(formattedDay, formattedDay)}
          </div>
        );
      })}
    </div>
  );
};

export const LeftChevron = (
  props: React.ElementConfig<'svg'>,
): React.Element<'svg'> => (
  <svg
    {...props}
    className={css.chevron}
    style={{fill: props.fill}}
    width="8"
    height="13"
    viewBox="0 0 8 13"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0.929688 6.3125C0.683594 6.55859 0.683594 6.96875 0.929688 7.21484L6.23438 12.5469C6.50781 12.793 6.91797 12.793 7.16406 12.5469L7.79297 11.918C8.03906 11.6719 8.03906 11.2617 7.79297 10.9883L3.58203 6.75L7.79297 2.53906C8.03906 2.26562 8.03906 1.85547 7.79297 1.60938L7.16406 0.980469C6.91797 0.734375 6.50781 0.734375 6.23438 0.980469L0.929688 6.3125Z" />
  </svg>
);

export const RightChevron = (
  props: React.ElementConfig<'svg'>,
): React.Element<'svg'> => (
  <svg
    {...props}
    className={css.chevron}
    style={{fill: props.fill}}
    width="8"
    height="13"
    viewBox="0 0 8 13"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M7.79297 7.21484C8.03906 6.96875 8.03906 6.55859 7.79297 6.3125L2.48828 0.980469C2.21484 0.734375 1.80469 0.734375 1.55859 0.980469L0.929688 1.60938C0.683594 1.85547 0.683594 2.26562 0.929688 2.53906L5.14062 6.75L0.929688 10.9883C0.683594 11.2617 0.683594 11.6719 0.929688 11.918L1.55859 12.5469C1.80469 12.793 2.21484 12.793 2.48828 12.5469L7.79297 7.21484Z" />
  </svg>
);

export const MonthPicker = ({
  monthIndex,
  year,
  onPreviousMonthClick,
  onNextMonthClick,
  color,
}: {
  monthIndex: number,
  year: number,
  onPreviousMonthClick: () => mixed,
  onNextMonthClick: () => mixed,
  color?: ?string,
}): React.Element<'div'> => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const date = new Date(year, monthIndex, 1);
  const headerMonth = format(date, 'MMMM');
  const headerYear = format(date, 'yyyy');
  return (
    <div>
      <h2 className={css.monthHeader}>{`${t(
        headerMonth,
        headerMonth,
      )} ${headerYear}`}</h2>
      <div className={css.monthControls}>
        <button className={css.monthSelector} onClick={onPreviousMonthClick}>
          <LeftChevron fill={color} />
        </button>
        <button className={css.monthSelector} onClick={onNextMonthClick}>
          <RightChevron fill={color} />
        </button>
      </div>
    </div>
  );
};

export const WeekPicker = ({
  date,
  size = 'medium',
  starts = 0,
  eventDates,
  onDateClick,
  setSelectedDate,
  selectedDate,
  monthIndex,
  onPreviousWeekClick,
  onNextWeekClick,
  color,
}: {
  date: Date,
  size?: string,
  eventDates: Date[],
  onDateClick?: (Date) => mixed,
  setSelectedDate?: (Date) => mixed,
  selectedDate?: Date,
  starts?: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  monthIndex?: number,
  onPreviousWeekClick: () => mixed,
  onNextWeekClick: () => mixed,
  color?: ?string,
}): React.Element<'div'> => {
  const week = eachWeekOfInterval(
    {start: date, end: date},
    {weekStartsOn: starts},
  );

  return (
    <div className={css.weekPicker}>
      <CalendarHeader className={size} date={date} starts={starts} />
      <div className={css.weekRow}>
        <button className={css.weekSelector} onClick={onPreviousWeekClick}>
          <LeftChevron fill={color} />
        </button>
        <WeekGrid
          className={size}
          firstDay={week[0]}
          eventDates={eventDates}
          onDateClick={onDateClick}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          monthIndex={monthIndex}
          color={color}
        />
        <button className={css.weekSelector} onClick={onNextWeekClick}>
          <RightChevron fill={color} />
        </button>
      </div>
    </div>
  );
};
